import React, { useEffect, useMemo, useState } from 'react';
import {
  getContacts,
  markContactAsRead,
  deleteContact,
} from '../../services/contactService';

const Icon = ({ name, size = 18, className = '' }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
    'aria-hidden': true,
  };

  const paths = {
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    inbox: <><path d="M4 4h16v15H4z" /><path d="M4 14h4l2 3h4l2-3h4" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    trash: <><path d="M4 7h16" /><path d="M10 11v6M14 11v6" /><path d="M6 7l1 13h10l1-13M9 7V4h6v3" /></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.9-4L3 10" /><path d="M3 5v5h5" /><path d="M4 13a8 8 0 0 0 14.9 4L21 14" /><path d="M21 19v-5h-5" /></>,
    reply: <><path d="M10 17 5 12l5-5" /><path d="M5 12h9a5 5 0 0 1 5 5v1" /></>,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    chevronDown: <path d="m6 9 6 6 6-6" />,
    more: <><circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /></>,
    user: <><circle cx="12" cy="8" r="3" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
    send: <><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
};

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [mobileDetail, setMobileDetail] = useState(false);

  const loadContacts = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getContacts();
      setContacts(data);

      if (data.length && !selectedId) {
        const firstUnread = data.find((c) => c.status === 'new');
        setSelectedId((firstUnread || data[0]).id);
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError(err.message || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await markContactAsRead(id);
      setContacts((prev) =>
        prev.map((c) => (c.id === id ? { ...c, status: 'read' } : c))
      );
    } catch (err) {
      console.error('Mark read failed:', err);
      setError('Could not update the message.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message permanently?')) return;

    try {
      await deleteContact(id);
      setContacts((prev) => {
        const remaining = prev.filter((c) => c.id !== id);
        if (remaining.length) {
          const next = remaining.find((c) => c.status === 'new') || remaining[0];
          setSelectedId(next.id);
        } else {
          setSelectedId(null);
        }
        return remaining;
      });
      setReplyText('');
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Could not delete the message.');
    }
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);

    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatShortDate = (ts) => {
    if (!ts) return '';
    const date = ts.toDate ? ts.toDate() : new Date(ts);

    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    });
  };

  const counts = useMemo(
    () => ({
      all: contacts.length,
      new: contacts.filter((c) => c.status === 'new').length,
      read: contacts.filter((c) => c.status === 'read').length,
    }),
    [contacts]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return contacts.filter((c) => {
      const matchesFilter = filter === 'all' || c.status === filter;
      const matchesSearch =
        !term ||
        [c.name, c.email, c.subject, c.message]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(term));

      return matchesFilter && matchesSearch;
    });
  }, [contacts, filter, search]);

  const selected = contacts.find((c) => c.id === selectedId) || null;

  useEffect(() => {
    if (selected && selected.status === 'new') {
      handleMarkRead(selected.id);
    }
  }, [selectedId]);

  const openMessage = (contact) => {
    setSelectedId(contact.id);
    setReplyText('');
    setMobileDetail(true);
  };

  const replySubject = selected
    ? `Re: ${selected.subject || 'Your enquiry'}`
    : '';

  const mailtoHref = selected
    ? `mailto:${selected.email}?subject=${encodeURIComponent(
        replySubject
      )}&body=${encodeURIComponent(replyText)}`
    : '#';

  if (loading) {
    return (
      <div className="min-h-[520px] flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-violet-600" />
          <p className="mt-4 text-sm text-slate-500">Loading messages…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-80px)] bg-slate-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-[1500px]">
        {/* Page header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-600">
                <Icon name="mail" size={20} />
              </div>
              <div>
                <span className="text-xl font-semibold tracking-tight text-slate-900">
                  Messages
                </span>
                <p className="text-sm text-slate-500">
                  Manage enquiries and respond to your customers.
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={loadContacts}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
          >
            <Icon name="refresh" size={16} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-5 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError('')}
              className="font-semibold hover:text-red-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            {
              label: 'Total messages',
              value: counts.all,
              hint: 'All enquiries',
              icon: 'inbox',
              tone: 'violet',
            },
            {
              label: 'Unread',
              value: counts.new,
              hint: 'Need your attention',
              icon: 'mail',
              tone: 'amber',
            },
            {
              label: 'Read',
              value: counts.read,
              hint: 'Already reviewed',
              icon: 'check',
              tone: 'emerald',
            },
          ].map((stat) => {
            const tones = {
              violet: 'bg-violet-50 text-violet-600',
              amber: 'bg-amber-50 text-amber-600',
              emerald: 'bg-emerald-50 text-emerald-600',
            };

            return (
              <div
                key={stat.label}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                      {stat.label}
                    </p>
                    <p className="mt-1 text-2xl font-semibold text-slate-900">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">{stat.hint}</p>
                  </div>
                  <div className={`rounded-xl p-2.5 ${tones[stat.tone]}`}>
                    <Icon name={stat.icon} size={18} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Message center */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Toolbar */}
          <div className="border-b border-slate-200 p-3 sm:p-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Icon
                  name="search"
                  size={17}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search messages, names or email addresses..."
                  className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:ring-2 focus:ring-violet-100"
                />
              </div>

              <div className="flex rounded-lg bg-slate-100 p-1">
                {[
                  ['all', 'All'],
                  ['new', 'Unread'],
                  ['read', 'Read'],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilter(value)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                      filter === value
                        ? 'bg-white text-violet-700 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {label}
                    <span className="ml-1.5 text-slate-400">
                      {counts[value]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Split inbox */}
          <div className="grid min-h-[600px] grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)]">
            {/* Message list */}
            <aside
              className={`border-slate-200 lg:border-r ${
                mobileDetail ? 'hidden lg:block' : 'block'
              }`}
            >
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Inbox
                </p>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                  {filtered.length}
                </span>
              </div>

              <div className="max-h-[600px] overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-6 py-16 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                      <Icon name="inbox" size={22} />
                    </div>
                    <span className="mt-4 text-sm font-semibold text-slate-700">
                      No messages found
                    </span>
                    <p className="mt-1 text-xs text-slate-400">
                      Try changing your filter or search.
                    </p>
                  </div>
                ) : (
                  filtered.map((contact) => {
                    const isSelected = selectedId === contact.id;
                    const isNew = contact.status === 'new';

                    return (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => openMessage(contact)}
                        className={`w-full border-b border-slate-100 px-4 py-4 text-left transition ${
                          isSelected
                            ? 'bg-violet-50/70'
                            : 'bg-white hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div
                            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                              isSelected
                                ? 'bg-violet-600 text-white'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {getInitials(contact.name)}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className={`truncate text-sm ${
                                    isNew
                                      ? 'font-semibold text-slate-900'
                                      : 'font-medium text-slate-700'
                                  }`}
                                >
                                  {contact.name}
                                </span>
                                {isNew && (
                                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-600" />
                                )}
                              </div>
                              <span className="flex-shrink-0 text-[11px] text-slate-400">
                                {formatShortDate(contact.createdAt)}
                              </span>
                            </div>

                            <p
                              className={`mt-1 truncate text-xs ${
                                isNew
                                  ? 'font-medium text-slate-700'
                                  : 'text-slate-500'
                              }`}
                            >
                              {contact.subject || 'No subject'}
                            </p>

                            <p className="mt-1 truncate text-xs text-slate-400">
                              {contact.message}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            {/* Message detail */}
            <section
              className={`min-w-0 ${
                mobileDetail ? 'block' : 'hidden lg:block'
              }`}
            >
              {selected ? (
                <div className="flex h-full min-h-[600px] flex-col">
                  {/* Detail header */}
                  <div className="border-b border-slate-200 px-5 py-4 sm:px-7">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setMobileDetail(false)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 lg:hidden"
                      >
                        <Icon name="chevronLeft" size={15} />
                        Back to inbox
                      </button>

                      <div className="ml-auto flex items-center gap-1">
                        {selected.status === 'new' && (
                          <button
                            type="button"
                            onClick={() => handleMarkRead(selected.id)}
                            title="Mark as read"
                            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                          >
                            <Icon name="check" size={17} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(selected.id)}
                          title="Delete message"
                          className="rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                        >
                          <Icon name="trash" size={17} />
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-700">
                        {getInitials(selected.name)}
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-base font-semibold text-slate-900">
                            {selected.name}
                          </span>
                          {selected.status === 'new' && (
                            <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                              Unread
                            </span>
                          )}
                        </div>

                        <p className="mt-0.5 text-xs text-slate-500">
                          {selected.email}
                        </p>

                        <p className="mt-2 text-xs text-slate-400">
                          {formatDate(selected.createdAt)}
                        </p>
                      </div>
                    </div>

                    <span className="mt-5 text-lg font-semibold text-slate-900">
                      {selected.subject || 'No subject'}
                    </span>
                  </div>

                  {/* Conversation */}
                  <div className="flex-1 overflow-y-auto px-5 py-6 sm:px-7">
                    <div className="max-w-3xl">
                      <div className="mb-2 flex items-center gap-2 text-xs text-slate-400">
                        <Icon name="mail" size={14} />
                        <span>{selected.email}</span>
                      </div>

                      <div className="rounded-2xl rounded-tl-md bg-slate-50 p-5 text-sm leading-7 text-slate-700">
                        <p className="whitespace-pre-wrap">{selected.message}</p>
                      </div>
                    </div>
                  </div>

                  {/* Reply composer */}
                  <div className="border-t border-slate-200 bg-white p-4 sm:p-5">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 focus-within:border-violet-300 focus-within:ring-2 focus-within:ring-violet-100">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={4}
                        placeholder={`Write a reply to ${selected.name}...`}
                        className="w-full resize-none bg-transparent px-4 py-3 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                      />

                      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-3 py-2">
                        <div className="text-[11px] text-slate-400">
                          Replying to {selected.email}
                        </div>

                        <a
                          href={mailtoHref}
                          onClick={() => {
                            if (selected.status === 'new') {
                              handleMarkRead(selected.id);
                            }
                          }}
                          className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-violet-700"
                        >
                          <Icon name="send" size={15} />
                          Open email reply
                        </a>
                      </div>
                    </div>

                    <p className="mt-2 px-1 text-[11px] text-slate-400">
                      Your default email application will open with the
                      recipient and subject already filled in.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex h-full min-h-[600px] items-center justify-center p-8 text-center">
                  <div>
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                      <Icon name="mail" size={28} />
                    </div>
                    <span className="mt-5 text-sm font-semibold text-slate-700">
                      Select a message
                    </span>
                    <p className="mt-1 max-w-xs text-xs leading-5 text-slate-400">
                      Choose an enquiry from your inbox to read the full
                      message and respond.
                    </p>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContacts;
